import { useEffect, useMemo, useState } from "react";
import { Button, DrawerBody, DrawerFooter, DrawerHeader, DrawerHeaderTitle, OverlayDrawer, Spinner } from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { DebtPaymentImputation } from "../../../../models/Entities/CashFlow/Payment/DebtPaymentImputation";
import { formatCurrencyAmountWithIssue } from "../../../../commons/currency/Currency.Functions";
import { candidateDebts, imputationTotals, inPaymentCurrency } from "../../CashFlow.Form.Logic";
import { PartyChip, Step } from "../../CashFlow.Form.Bits";
import { MEDICAL_CENTER_NAME } from "../../General/Cashflow.General.Functions";
import { ImputationPicker } from "../Payment.Form/Payment.Imputation.Picker";
import * as ComponentFunctions from './PaymentImputation.Form.Function';
import "../../CashFlow.Form.Styles.css";

const fmt = (amount: number | undefined | null, currency: string | undefined | null) =>
    formatCurrencyAmountWithIssue(amount ?? 0, currency || 'ARS');

/**
 * Aplicar lo que un cobro (o pago) todavía tiene sin imputar a las deudas de
 * la misma contraparte. Mismo selector que al registrar el movimiento.
 */
const PaymentImputationFormComponent = (props: ComponentFunctions.Props) => {
    const [t] = useTranslation();
    const [imputations, setImputations] = useState<DebtPaymentImputation[]>([]);
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [remaining, setRemaining] = useState<number>(0);
    const [saveError, setSaveError] = useState<string | undefined>(undefined);

    const payment = props.payment;
    const currency = payment?.currencyCode || 'ARS';
    const pending = Math.round((payment?.pendingIndictmentAmount ?? 0) * 100) / 100;
    const counterpartId = payment ? (props.CenterCollects ? payment.payer : payment.receiver) : undefined;
    const counterpartName = props.NameOf(counterpartId);

    // Al abrir: deudas y nombres, si todavía no están.
    useEffect(() => {
        if (!props.isOpen || !payment) return;
        setImputations([]);
        setSubmitted(false);
        setSaveError(undefined);
        props.GetAllPersons();
        if (!props.debtsLoaded && !props.isLoadingDebts) {
            if (props.CenterCollects) props.GetAllDebtsByCreditor(props.OwnerId, undefined);
            else props.GetAllDebtsByDebtor(props.OwnerId, undefined);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen, payment?.id]);

    // Se aplica una deuda por pedido; cuando terminan todos, se cierra o se avisa.
    useEffect(() => {
        if (!submitted || remaining > 0 || props.isCreatingImputation) return;
        if (props.failOnCreatingImputation) {
            setSaveError(props.error?.ErrorMessage || t('cashflow.imputations.createError', 'No se pudo guardar la imputación.'));
            setSubmitted(false);
            return;
        }
        if (payment) props.GetPaymentImputations(payment.id);
        setSubmitted(false);
        props.onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitted, remaining, props.isCreatingImputation, props.failOnCreatingImputation]);

    const candidates = useMemo(
        () => candidateDebts(props.CandidateDebts, undefined, () => ''),
        [props.CandidateDebts]
    );

    const totals = imputationTotals(imputations, pending);
    const canApply = imputations.length > 0 && !totals.over && imputations.every((item) => item.exchangeRate > 0) && !props.isCreatingImputation && !submitted;

    const apply = async () => {
        if (!payment || !canApply) return;
        setSaveError(undefined);
        setSubmitted(true);
        setRemaining(1);
        // Todas las deudas en un solo pedido. El picker trabaja en moneda de la deuda; la API espera el importe en moneda del pago.
        const debtsPaymentImputation = imputations.map((item) => ({
            debtId: item.debtId,
            amount: Math.round(inPaymentCurrency(item) * 100) / 100,
            exchangeRate: item.exchangeRate
        }));
        const total = Math.round(debtsPaymentImputation.reduce((acc, item) => acc + item.amount, 0) * 100) / 100;
        await props.CreatePaymentImputation({ paymentId: payment.id, id: 0, amount: total, currencyCode: currency, debtsPaymentImputation });
        setRemaining(0);
    };

    const close = () => { setImputations([]); setSubmitted(false); props.onClose(); };

    if (!payment) return null;

    const kind = props.CenterCollects ? 'cobro' : 'pago';
    const imputed = payment.imputedAmount ?? 0;
    const imputedPct = payment.amount > 0 ? Math.min(100, (imputed / payment.amount) * 100) : 0;

    return (
        <OverlayDrawer open={props.isOpen} onOpenChange={(_, data) => { if (!data.open) close(); }} position="end" size="large">
            <DrawerHeader>
                <DrawerHeaderTitle action={<Button appearance="subtle" aria-label={t('common.close', 'Cerrar')} icon={<Dismiss24Regular />} onClick={close} />}>
                    <span className="cff-title">{props.title ?? t('cashflow.imputations.formTitle', 'Imputar saldo pendiente')}</span>
                </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody>
                <div className="cff-root">
                    {saveError && (
                        <div className="cff-banner" role="alert">
                            <strong>No se pudo aplicar.</strong>
                            <span>{saveError}</span>
                        </div>
                    )}

                    {/* Qué se está imputando */}
                    <section className="cff-step">
                        <div className="cff-parties">
                            <PartyChip role={props.CenterCollects ? 'Pagó' : 'Cobró'} name={counterpartName} />
                            <span className="cff-parties__arrow" aria-hidden>→</span>
                            <PartyChip role={props.CenterCollects ? 'Cobró' : 'Pagó'} name={MEDICAL_CENTER_NAME} />
                        </div>
                        <div className="cff-summary">
                            <div className="cff-summary__item">
                                <div className="cff-summary__label">{props.CenterCollects ? 'Cobro' : 'Pago'} del {payment.paymentDate ? moment(payment.paymentDate).format('DD/MM/YYYY') : '—'}</div>
                                <div className="cff-summary__value cff-num">{fmt(payment.amount, currency)}</div>
                            </div>
                            <div className="cff-summary__item">
                                <div className="cff-summary__label">Ya imputado · {imputedPct.toFixed(0)}%</div>
                                <div className="cff-summary__value cff-num">{fmt(imputed, currency)}</div>
                            </div>
                            <div className="cff-summary__item">
                                <div className="cff-summary__label">Sin imputar</div>
                                <div className="cff-summary__value cff-num" style={{ color: '#a14f05' }}>{fmt(pending, currency)}</div>
                            </div>
                        </div>
                    </section>

                    {/* A qué deudas */}
                    {pending > 0 ? (
                        <Step n={1} title="¿A qué deudas se aplica?"
                            hint={<>Pendientes de {counterpartName}, más viejas primero. Sólo se puede aplicar hasta {fmt(pending, currency)}.</>}>
                            {props.isLoadingDebts && candidates.length === 0
                                ? <div className="cff-state"><Spinner size="extra-tiny" /> Buscando deudas…</div>
                                : (
                                    <ImputationPicker
                                        debts={candidates}
                                        paymentAmount={pending}
                                        paymentCurrency={currency}
                                        counterpartName={counterpartName}
                                        value={imputations}
                                        onChange={setImputations}
                                    />
                                )}
                        </Step>
                    ) : (
                        <div className="cff-banner cff-banner--warn">
                            <strong>Este {kind} ya está imputado por completo.</strong>
                            <span>No queda saldo para aplicar a otras deudas.</span>
                        </div>
                    )}
                </div>
            </DrawerBody>
            <DrawerFooter>
                <div className="cff-footer">
                    {imputations.length > 0 && (
                        <span className="cff-footer__summary">
                            Se aplican <b className="cff-num">{fmt(totals.imputed, currency)}</b> a {imputations.length} {imputations.length === 1 ? 'deuda' : 'deudas'}
                            {totals.remaining > 0.005 && <> · quedan {fmt(totals.remaining, currency)} sin imputar</>}
                        </span>
                    )}
                    <button type="button" className="cff-secondary" onClick={close} disabled={submitted}>{t('cashflow.imputations.cancel', 'Cancelar')}</button>
                    <button type="button" className="cff-primary" onClick={apply} disabled={!canApply}>
                        {submitted && <Spinner size="extra-tiny" />} Aplicar a las deudas
                    </button>
                </div>
            </DrawerFooter>
        </OverlayDrawer>
    );
};

export default ComponentFunctions.connector(PaymentImputationFormComponent);
